import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle, FileText, FolderSearch, Zap, CheckCircle2, XCircle, Unlink as UnlinkIcon, Wand, Upload, Info, Pencil, Check, X } from 'lucide-react';
import CandidateRow from '../components/CandidateRow';
import VerdictNote, { byVerdict, type Verdict } from '../components/VerdictNote';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useToast } from '../contexts/ToastContext';
import { usePanel } from '../contexts/PanelContext';
import { generateMdx } from '../lib/markdown';
import { generateLabel, generationNote } from '../lib/generation';
import { formatDateTime } from '../lib/text';
import { MetadataInfo } from '../lib/files';
import SourceLink from '../components/SourceLink';
import type { Field, DocumentPriority, GeneratorReadiness } from '../lib/types';
import { FieldInput } from '../components/SectionQuestionsTab';
import { PageLoader } from '../components/PageLoader';
import { PageError } from '../components/PageError';
import BackButton from '../components/BackButton';
import ConnectSourceButton from '../components/ConnectSourceButton';
import DismissableInfoCard from '../components/DismissableInfoCard';
import { Pill, priorityStyles, statusStyles, type DocStatus } from '../components/DocTypeBlock';
import SourceLogo from '../components/SourceLogo';
import { useAgentCard } from '../components/AgentDock';

interface TypeDef {
    name: string;
    description?: string;
    help?: string;
    notes?: string | null;
    priority: DocumentPriority;
    relevant_when?: Record<string, any>;
    fields?: string[];
    file_types?: string[];
    match_terms?: string[];
}

interface DiscoverMatch extends Verdict {
    id: string;
    source_filename: string;
    source_path: string | null;
    file_date: string | null;
    // Byte-identical copies in the same source, collapsed into this candidate; linking it covers them all.
    duplicates?: { id: string; source_filename: string | null; source_path: string | null }[];
}

interface DiscoverLinked {
    file_id: string;
    version: number;
    latest_version: number;
    source_filename: string;
    source_path: string | null;
    is_generated: boolean;
}

interface DiscoverFolder extends Verdict {
    source_id: string;
    path: string;
    document_type_id: string | null;
    document_type: string | null;
    file_count: number;
}

interface DiscoverSource {
    id: string;
    source: string;
    source_type: string;
    linked: DiscoverLinked[];
    matches: DiscoverMatch[];
    folders: DiscoverFolder[];
}

interface TypeDetail {
    def: TypeDef;
    status: DocStatus;
    relevance_fields: Record<string, Field>;
    sources: DiscoverSource[];
    generated: GeneratorReadiness | null;
}

type RelevanceState = 'relevant' | 'not_relevant' | 'pending';

// Mirrors backend `document_relevance_status`.
function evaluateRelevance(
    relevantWhen: Record<string, any> | undefined,
    data: Record<string, any>,
): RelevanceState {
    if (!relevantWhen) return 'relevant';
    let anyUnanswered = false;
    for (const [fieldId, expected] of Object.entries(relevantWhen)) {
        const actual = data[fieldId];
        if (actual === null || actual === undefined || actual === '') {
            anyUnanswered = true;
            continue;
        }
        if (expected === '*') continue;
        let matches: boolean;
        if (Array.isArray(expected)) {
            matches = Array.isArray(actual)
                ? actual.some(v => expected.includes(v))
                : expected.includes(actual);
        } else if (Array.isArray(actual)) {
            matches = actual.includes(expected);
        } else {
            matches = actual === expected;
        }
        if (!matches) return 'not_relevant';
    }
    return anyUnanswered ? 'pending' : 'relevant';
}

const relevanceStyles: Record<RelevanceState, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
    relevant: {
        label: 'Relevant - this document applies to your business',
        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        Icon: CheckCircle2,
    },
    not_relevant: {
        label: 'Not relevant - this document does not apply to your business',
        cls: 'bg-red-50 text-red-700 border-red-200',
        Icon: XCircle,
    },
    pending: {
        label: 'Relevance not yet determined - answer the questions above',
        cls: 'bg-amber-50 text-amber-700 border-amber-200',
        Icon: AlertCircle,
    },
};

export default function DocumentTypeDetailPage() {
    const { authFetch } = useAuth();
    const { fields: fieldValues, saveField: saveFieldGlobal, refresh: refreshWorkspace } = useWorkspace();
    const { startChat } = useAgentCard();
    const { showToast } = useToast();
    const { openPanel } = usePanel();
    const location = useLocation();

    const pathParts = location.pathname.split('/').filter(p => p);
    const typeId = pathParts[1] ?? '';

    const [detail, setDetail] = useState<TypeDetail | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [questionsExpanded, setQuestionsExpanded] = useState(false);
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesDraft, setNotesDraft] = useState('');
    const [savingNotes, setSavingNotes] = useState(false);
    const [matchFilters, setMatchFilters] = useState<Record<string, string>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    const loadType = useCallback(async (silent = false) => {
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await authFetch(`/workspace/types/${typeId}`);
            if (res.status === 404) {
                setError(`Unknown document type: ${typeId}`);
                setDetail(null);
                return;
            }
            if (!res.ok) throw new Error('Failed to load document type');
            setDetail(await res.json());
        } catch (err) {
            if (!silent) setError(err instanceof Error ? err.message : 'Failed to load document type');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [typeId]);

    useEffect(() => { loadType(); }, [loadType]);

    // Generation runs as a background job the agent starts; poll while one is in flight so the result lands here.
    const generatingActive = !!detail?.generated?.active_job;
    useEffect(() => {
        if (!generatingActive) return;
        const interval = setInterval(() => {
            Promise.all([loadType(true), refreshWorkspace()]);
        }, 4000);
        return () => clearInterval(interval);
    }, [generatingActive, loadType, refreshWorkspace]);

    const saveField = async (fieldId: string, value: any) => {
        try {
            await saveFieldGlobal(fieldId, value);
        } catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to save answer', 'error');
        }
    };

    const handleLink = async (definitionId: string, fileId: string) => {
        await authFetch(`/workspace/links/${definitionId}/${fileId}`, { method: 'POST' });
        await Promise.all([loadType(true), refreshWorkspace()]);
    };

    const handleLinkFolder = async (definitionId: string, sourceId: string, path: string) => {
        await authFetch(
            `/workspace/folder-links/${definitionId}?source_id=${encodeURIComponent(sourceId)}&path=${encodeURIComponent(path)}`,
            { method: 'POST' },
        );
        await Promise.all([loadType(true), refreshWorkspace()]);
    };

    const handleUnlink = async (definitionId: string, fileId: string) => {
        if (!window.confirm('Unlink this file from the document type?')) return;
        await authFetch(`/workspace/links/${definitionId}/${fileId}`, { method: 'DELETE' });
        await Promise.all([loadType(true), refreshWorkspace()]);
    };

    // Generation runs through the agent chat; its generate_document tool starts the job.
    const handleKnowledgeGenerate = () => {
        const g = detail?.generated;
        startChat(g?.skill ?? undefined, {
            ask: `Generate the "${detail?.def.name ?? typeId}" document (document type ${typeId}).`,
            beside: true,
        });
    };

    const startEditNotes = () => {
        setNotesDraft(detail?.def.notes ?? '');
        setEditingNotes(true);
    };

    const cancelEditNotes = () => {
        setEditingNotes(false);
        setNotesDraft('');
    };

    const saveNotes = async () => {
        setSavingNotes(true);
        try {
            const res = await authFetch(`/workspace/types/${typeId}/notes`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notes: notesDraft }),
            });
            if (!res.ok) throw new Error('Failed to save notes');
            setEditingNotes(false);
            await Promise.all([loadType(true), refreshWorkspace()]);
        } catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to save notes', 'error');
        } finally {
            setSavingNotes(false);
        }
    };

    const handleUpload = async (file: File) => {
        if (!file || file.size === 0) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await authFetch(`/workspace/types/${typeId}/upload`, {
                method: 'POST',
                body: formData,
            });
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to upload file');
            }
            showToast(`Uploaded "${file.name}" - processing in background`, 'success');
            await Promise.all([loadType(true), refreshWorkspace()]);
        } catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to upload', 'error');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    if (loading) return <PageLoader label="Loading document type..." />;
    if (error || !detail) return <PageError title="Document type not found" message={error || 'Unknown document type'} />;

    const { def, status: docStatus, relevance_fields: fieldsLookup, sources, generated } = detail;

    const relevanceFieldIds = Object.keys(def.relevant_when ?? {});
    const relevanceState = evaluateRelevance(def.relevant_when, fieldValues);
    const relevanceStyle = relevanceStyles[relevanceState];

    const fileTypes = def.file_types ?? [];
    const matchTerms = def.match_terms ?? [];
    const fields = def.fields ?? [];

    const metadata: Record<string, any> = { Identifier: typeId };
    if (fileTypes.length > 0) metadata['File types'] = fileTypes.join(', ');
    if (matchTerms.length > 0) metadata['Match terms'] = matchTerms.join(', ');
    if (fields.length > 0) metadata['Captured fields'] = fields.join(', ');

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <div className="flex items-start gap-4">
                    <BackButton className="mt-3" />
                    <div className="navaris-tile-tint w-12 h-12 rounded-xl flex border border-softgrey items-center justify-center shrink-0">
                        <FileText className="w-6 h-6 text-brand" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-graphite wrap-break-word">{def.name}</h3>
                            <Pill style={priorityStyles[def.priority]} shrink />
                            <Pill style={statusStyles[docStatus]} shrink />
                        </div>
                        {(def.description || def.help) && (
                            <p className="text-sm text-coolslate mt-1">
                                {def.description}
                                {def.help && (
                                    <button
                                        type="button"
                                        onClick={() => openPanel(
                                            def.name,
                                            <div className="text-sm text-coolslate leading-relaxed" dangerouslySetInnerHTML={{ __html: generateMdx(def.help!) }} />
                                        )}
                                        className="ml-1.5 text-sm font-medium text-brand hover:text-brand-dark transition-colors cursor-pointer"
                                    >
                                        Read more →
                                    </button>
                                )}
                            </p>
                        )}
                    </div>
                    <MetadataInfo metadata={metadata} />
                </div>
            </div>

            <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-sm font-semibold text-graphite">Notes</h4>
                    {!editingNotes && (
                        <button
                            type="button"
                            onClick={startEditNotes}
                            className="p-1 text-coolslate hover:text-brand-dark transition-colors cursor-pointer"
                            title="Edit notes"
                            aria-label="Edit notes"
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
                {editingNotes ? (
                    <div>
                        <textarea
                            value={notesDraft}
                            onChange={e => setNotesDraft(e.target.value)}
                            rows={4}
                            placeholder="Add a note about this document type…"
                            className="w-full px-3 py-2 text-sm border border-softgrey rounded-lg focus:outline-none focus:border-brand bg-white text-graphite placeholder-coolslate/60 resize-y"
                            autoFocus
                        />
                        <div className="mt-2 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={saveNotes}
                                disabled={savingNotes}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-md transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {savingNotes ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={cancelEditNotes}
                                disabled={savingNotes}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-coolslate hover:text-graphite rounded-md transition-colors cursor-pointer disabled:opacity-60"
                            >
                                <X className="w-3.5 h-3.5" />
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : def.notes ? (
                    <div className="flex items-start gap-2 px-3.5 py-2.5 bg-sky-50 border border-sky-200 rounded-lg text-[13px] text-sky-900 leading-relaxed">
                        <Info className="w-3.5 h-3.5 text-sky-600 shrink-0 mt-0.5" />
                        <span className="whitespace-pre-wrap min-w-0">{def.notes}</span>
                    </div>
                ) : (
                    <p className="text-sm text-coolslate/70 italic">No notes yet.</p>
                )}
            </div>

            {relevanceFieldIds.length > 0 && (() => {
                const showQuestions = relevanceState === 'pending' || questionsExpanded;
                return (
                    <div className="mb-10">
                        {showQuestions && (
                            <div className="border border-softgrey rounded-xl bg-white p-6 mb-3">
                                <div className="space-y-6">
                                    {relevanceFieldIds.map(fid => {
                                        const field = fieldsLookup[fid];
                                        if (!field) return null;
                                        return (
                                            <FieldInput
                                                key={fid}
                                                field={field}
                                                value={fieldValues[fid]}
                                                onChange={(v) => saveField(fid, v)}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <div className={`flex items-center gap-2 px-3.5 py-2.5 rounded-lg border text-sm font-medium ${relevanceStyle.cls}`}>
                            <relevanceStyle.Icon className="w-4 h-4 shrink-0" />
                            <span className="flex-1">{relevanceStyle.label}</span>
                            {relevanceState !== 'pending' && (
                                <button
                                    type="button"
                                    onClick={() => setQuestionsExpanded(v => !v)}
                                    aria-label={questionsExpanded ? 'Hide questions' : 'Show questions'}
                                    aria-expanded={questionsExpanded}
                                    className="opacity-70 hover:opacity-100 transition-opacity cursor-pointer shrink-0"
                                >
                                    <Info className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })()}

            {generated && (
                <div className="mb-10">
                    <div className="mb-4">
                        <h4 className="navaris-h3">Generate</h4>
                        <p className="text-[13px] text-coolslate mt-1">
                            {generationNote(generated)} Generating creates the document and links it here.
                        </p>
                    </div>
                    <div className="border border-softgrey rounded-xl p-4 flex items-start justify-between gap-4">
                        <div className="min-w-0 text-[13px]">
                            {generated.sources.length > 0 ? (
                                <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 text-coolslate">
                                    <span className="text-coolslate/70">Built from</span>
                                    {generated.sources.map((s, i) => (
                                        <span key={s.id}>
                                            <SourceLink source={s}>{s.name ?? s.id}</SourceLink>
                                            {i < generated.sources.length - 1 ? ',' : ''}
                                        </span>
                                    ))}
                                </span>
                            ) : (
                                <span className="text-coolslate/70">No data available yet</span>
                            )}
                            {!generated.ready && generated.missing.length > 0 && (
                                <p className="mt-1.5 text-[12px] text-state-error">Needs data for: {generated.missing.join(', ')}</p>
                            )}
                            {generated.linked && (
                                <p className="mt-1.5 text-[12px] text-state-success">Generated and linked.</p>
                            )}
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                            {generated.file_id && (
                                <Link to={`/files/${generated.file_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-graphite border border-softgrey rounded-lg hover:bg-offwhite transition-colors">
                                    <FileText className="w-3.5 h-3.5" /> View
                                </Link>
                            )}
                            <button
                                onClick={handleKnowledgeGenerate}
                                disabled={!generated.ready || !!generated.active_job}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={!generated.ready ? 'Not enough data yet' : undefined}
                            >
                                {generated.active_job
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Wand className="w-3.5 h-3.5" />}
                                {generated.active_job ? 'Generating…' : generateLabel(generated)}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-10">
                {sources.length > 0 && (
                    <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
                        <div>
                            <h4 className="navaris-h3">Source files</h4>
                            <p className="text-[13px] text-coolslate mt-1">
                                Documents linked or matched for this type.
                            </p>
                        </div>
                    </div>
                )}

                {sources.length === 0 ? (
                    <div className="flex flex-wrap gap-4">
                        <ConnectSourceButton redirectTo={location.pathname + location.search} />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <DismissableInfoCard
                            storageKey="docType.candidatesIntroDismissed"
                            title="Linking candidate files"
                            icon={FolderSearch}
                        >
                            These are files across your connected sources whose names match this
                            document type but which Navaris was not sure of. Click <strong>Link</strong> to
                            file one here. Use <strong>Browse</strong> to search a source for more.
                        </DismissableInfoCard>
                        {sources.map(source => {
                            const linked = source.linked ?? [];
                            const matches = source.matches ?? [];
                            const folders = source.folders ?? [];
                            const filterRaw = matchFilters[source.id] ?? '';
                            // Alphanumerics only, so "ar-aging" matches "AR Aging Report", "AR_Aging", "ar.aging.xlsx".
                            const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
                            const filter = normalize(filterRaw);
                            const filteredMatches = [...(filter
                                ? matches.filter(m =>
                                      normalize(m.source_filename ?? '').includes(filter) ||
                                      normalize(m.source_path ?? '').includes(filter) ||
                                      (m.duplicates ?? []).some(d =>
                                          normalize(d.source_filename ?? '').includes(filter) ||
                                          normalize(d.source_path ?? '').includes(filter)
                                      )
                                  )
                                : matches)].sort(byVerdict);
                            const showMatchFilter = matches.length > 10;

                            return (
                                <div key={source.id} className="border border-softgrey rounded-xl overflow-hidden">
                                    <div className="bg-offwhite/50">
                                        <div className="flex items-center gap-3.5 px-4 py-3.5">
                                            <div className="w-9 h-9 rounded-lg border border-softgrey bg-white flex items-center justify-center shrink-0">
                                                <SourceLogo type={source.source_type} size="md" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className="text-sm font-semibold text-graphite">{source.source}</span>
                                            </div>
                                            <Link
                                                to={`/connections/${source.id}?tab=files&type=${encodeURIComponent(typeId)}`}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-brand bg-brand/8 hover:bg-brand/15 border border-brand/20 rounded-md transition-colors cursor-pointer shrink-0"
                                            >
                                                <FolderSearch className="w-3 h-3" />
                                                Browse
                                            </Link>
                                        </div>
                                        {showMatchFilter && (
                                            <div className="px-4 pb-3">
                                                <input
                                                    type="text"
                                                    value={filterRaw}
                                                    onChange={(e) => setMatchFilters(prev => ({ ...prev, [source.id]: e.target.value }))}
                                                    placeholder={`Filter ${matches.length} candidates…`}
                                                    className="w-full px-3 py-2 text-[13px] border border-softgrey rounded-lg bg-white text-graphite placeholder-coolslate focus:outline-none focus:border-brand/40 transition-colors"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {(linked.length + matches.length + folders.length) > 0 && (
                                        <div className="px-4 py-3 space-y-1.5 max-h-80 overflow-y-auto">
                                            {linked.map(ld => (
                                                <div
                                                    key={`linked-${ld.file_id}`}
                                                    className="flex items-center gap-3 px-3.5 py-3 border border-sage/30 bg-sage/5 rounded-lg"
                                                >
                                                    {ld.is_generated && (
                                                        <Zap className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                                    )}
                                                    <Link to={`/files/${ld.file_id}`} className="flex-1 min-w-0 hover:opacity-70 transition-opacity">
                                                        <span className="text-sm font-medium text-graphite truncate block">
                                                            {ld.is_generated ? def.name : ld.source_filename}
                                                            {ld.version > 1 && (
                                                                <span className="ml-1.5 text-[11px] font-mono text-coolslate bg-offwhite border border-softgrey rounded px-1 py-px">
                                                                    v{ld.version}
                                                                </span>
                                                            )}
                                                        </span>
                                                        {!ld.is_generated && ld.source_path && (
                                                            <span className="text-[12px] text-coolslate truncate block mt-0.5">{ld.source_path}</span>
                                                        )}
                                                    </Link>
                                                    {ld.latest_version > ld.version && (
                                                        <button
                                                            onClick={() => handleLink(typeId, ld.file_id)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition-colors cursor-pointer shrink-0"
                                                        >
                                                            <Wand className="w-3 h-3" />
                                                            New version
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleUnlink(typeId, ld.file_id)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition-colors cursor-pointer shrink-0"
                                                    >
                                                        <UnlinkIcon className="w-3 h-3" />
                                                        Unlink
                                                    </button>
                                                </div>
                                            ))}

                                            {filteredMatches.map(result => {
                                                return (
                                                    <CandidateRow
                                                        key={`match-${result.id}`}
                                                        kind="file"
                                                        title={result.source_filename}
                                                        titleHref={`/files/${result.id}?type=${encodeURIComponent(typeId)}`}
                                                        subtitle={result.source_path || undefined}
                                                        meta={result.file_date ? formatDateTime(result.file_date, false) : undefined}
                                                        primary={{ label: 'Link', recommended: result.verdict === 'file' && result.verdict_type === typeId, onClick: () => handleLink(typeId, result.id) }}
                                                    >
                                                        <VerdictNote verdict={result} typeId={typeId} />
                                                        {result.duplicates && result.duplicates.length > 0 && (
                                                            <div className="mt-1 text-[11px] text-coolslate">
                                                                <span className="text-coolslate/80">
                                                                    Same file also in {result.duplicates.length} other {result.duplicates.length === 1 ? 'location' : 'locations'}:
                                                                </span>
                                                                <ul className="mt-0.5 space-y-0.5">
                                                                    {result.duplicates.map(dup => (
                                                                        <li
                                                                            key={dup.id}
                                                                            className="truncate"
                                                                            title={`${dup.source_path ? dup.source_path + '/' : ''}${dup.source_filename ?? ''}`}
                                                                        >
                                                                            {dup.source_path ? `${dup.source_path}/` : ''}{dup.source_filename}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </CandidateRow>
                                                );
                                            })}
                                            {[...folders].sort(byVerdict).map(f => (
                                                <CandidateRow
                                                    key={`folder-${f.path}`}
                                                    kind="folder"
                                                    title={f.path.split('/').filter(Boolean).slice(-1)[0] || f.path}
                                                    subtitle={`${f.path} · ${f.file_count} file${f.file_count === 1 ? '' : 's'} · links future files too`}
                                                    primary={{ label: 'Link', recommended: f.verdict === 'file' && f.verdict_type === typeId, onClick: () => handleLinkFolder(typeId, f.source_id, f.path) }}
                                                >
                                                    <VerdictNote verdict={f} typeId={typeId} />
                                                </CandidateRow>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div>
                <div className="mb-4">
                    <h4 className="navaris-h3">Upload a file</h4>
                    <p className="text-[13px] text-coolslate mt-1">
                        Upload a file from your computer and link it directly to this document type.
                    </p>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.txt,.md,.doc,.docx,.csv,.json,.log,.xlsx,.xls,.pptx"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUpload(f);
                    }}
                />
                <div
                    onDragOver={(e) => { e.preventDefault(); if (!uploading) setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        if (uploading) return;
                        const f = e.dataTransfer.files?.[0];
                        if (f) handleUpload(f);
                    }}
                    onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
                    className={`border-2 border-dashed rounded-xl py-8 px-6 text-center transition-all ${uploading ? 'cursor-wait opacity-70' : 'cursor-pointer'} ${isDragging ? 'border-brand bg-brand/5' : 'border-softgrey bg-offwhite hover:bg-offwhite/70'}`}
                >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 border border-softgrey ${isDragging ? 'bg-brand/10 text-brand' : 'bg-white text-coolslate'}`}>
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                    </div>
                    {uploading ? (
                        <p className="text-sm font-medium text-graphite">Uploading…</p>
                    ) : (
                        <>
                            <p className={`text-sm font-medium ${isDragging ? 'text-brand' : 'text-graphite'}`}>
                                {isDragging ? 'Drop your file here' : 'Drag and drop, or click to browse'}
                            </p>
                            <p className="text-[12px] text-coolslate mt-1">
                                PDF, DOCX, XLSX, PPTX, CSV, TXT, MD, JSON (max 25MB)
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
